import { FC, useState, useEffect } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  Typography,
  CircularProgress,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { auth, onboarding, getSubscriptionInfo } from '../services/api';
import type { ChannelInfo, ChannelInfo_post, InstagramStatus } from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';
import { enqueueSnackbar } from 'notistack';
import { format as formatJalali } from 'date-fns-jalali';
import { format as formatGregorian } from 'date-fns';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import HelpIcon from '@mui/icons-material/Help';

interface ChannelForm {
  instagram_enabled: boolean;
  telegram_bot_token: string;
  telegram_enabled: boolean;
  website_chatbox_id: string;
  website_chatbox_enabled: boolean;
  website_address: string;
  bale_bot_token: string;
  bale_enabled: boolean;
  kavenegar_api_key: string;
  kavenegar_enabled: boolean;
}



export const ChannelContent: FC = () => {
  const { t, currentLanguage } = useTranslation();
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [userID , setUserID] = useState<string>('');
  const [formData, setFormData] = useState<ChannelForm>({
    instagram_enabled: false,
    telegram_bot_token: '',
    telegram_enabled: false,
    website_chatbox_id: '',
    website_chatbox_enabled: true,
    website_address: '',
    bale_bot_token: '',
    bale_enabled: false,
    kavenegar_api_key: '',
    kavenegar_enabled: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [websiteAddressError, setWebsiteAddressError] = useState('');
  const [instagramStatus, setInstagramStatus] = useState<InstagramStatus>({
    enabled: false,
    connected: false,
    username: null,
    user_id: null,
    token_expires_at: null,
    token_valid: false,
  });
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<'seller' | 'assistant' | null>(null);
  const [modalOpen, setModalOpen] = useState({
    instagram: false,
    telegram: false,
    website: false,
    bale: false,
  });
  let flag = false;

  // Helper function to format date based on language
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLanguage === 'fa' 
      ? formatJalali(date, 'yyyy/MM/dd')
      : formatGregorian(date, 'yyyy/MM/dd');
  };

  // Modal handlers
  const handleOpenModal = (modalType: keyof typeof modalOpen) => {
    setModalOpen(prev => ({ ...prev, [modalType]: true }));
  };

  const handleCloseModal = (modalType: keyof typeof modalOpen) => {
    setModalOpen(prev => ({ ...prev, [modalType]: false }));
  };

  // Handle switch change with modal opening or backend save
  const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>, modalType: keyof typeof modalOpen) => {
    const { name, checked } = e.target;
    
    // If switching from inactive to active, open modal (don't update state yet)
    if (checked) {
      handleOpenModal(modalType);
    } else {
      // If switching from active to inactive, update state and save to backend immediately
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
      
      try {
        setLoading(true);
        const channelInfo: ChannelInfo_post = {
          telegram_enabled: modalType === 'telegram' ? checked : formData.telegram_enabled,
          telegram_bot_token: formData.telegram_bot_token.trim(),
          website_chatbox_enabled: modalType === 'website' ? checked : formData.website_chatbox_enabled,
          website_address: formData.website_address.trim(),
          bale_enabled: modalType === 'bale' ? checked : formData.bale_enabled,
          bale_bot_token: formData.bale_bot_token.trim(),
          kavenegar_enabled: formData.kavenegar_enabled,
          kavenegar_api_key: formData.kavenegar_api_key.trim(),
          instagram_enabled: modalType === 'instagram' ? checked : formData.instagram_enabled,
          instagram_username: null,
          instagram_password: null,
        };
        
        await onboarding.saveChannelInfo(channelInfo);
        enqueueSnackbar(t('channel.success'), { 
          variant: 'success',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } catch (err: any) {
        console.error(`Error saving ${modalType} settings:`, err);
        enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
        // Revert the switch state if save failed
        setFormData(prev => ({
          ...prev,
          [name]: !checked,
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  // Individual save functions for each channel type
  const saveInstagramSettings = async () => {
    try {
      setLoading(true);
      
      // Update the switch state to enabled
      setFormData(prev => ({
        ...prev,
        instagram_enabled: true,
      }));
      
      const channelInfo: ChannelInfo_post = {
        telegram_enabled: formData.telegram_enabled,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: formData.website_chatbox_enabled,
        website_address: formData.website_address.trim(),
        bale_enabled: formData.bale_enabled,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: true,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      enqueueSnackbar(t('channel.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      handleCloseModal('instagram');
    } catch (err: any) {
      console.error('Error saving Instagram settings:', err);
      enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      // Revert the switch state if save failed
      setFormData(prev => ({
        ...prev,
        instagram_enabled: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  const saveTelegramSettings = async () => {
    try {
      setLoading(true);
      
      // Update the switch state to enabled
      setFormData(prev => ({
        ...prev,
        telegram_enabled: true,
      }));
      
      const channelInfo: ChannelInfo_post = {
        telegram_enabled: true,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: formData.website_chatbox_enabled,
        website_address: formData.website_address.trim(),
        bale_enabled: formData.bale_enabled,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: formData.instagram_enabled,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      enqueueSnackbar(t('channel.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      handleCloseModal('telegram');
    } catch (err: any) {
      console.error('Error saving Telegram settings:', err);
      enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      // Revert the switch state if save failed
      setFormData(prev => ({
        ...prev,
        telegram_enabled: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  const saveWebsiteSettings = async () => {
    try {
      setLoading(true);
      
      // Determine the website address to use
      let websiteAddress = formData.website_address.trim();
      
      // Validate website address before submission
      if (websiteAddress && !validateUrl(websiteAddress)) {
        setWebsiteAddressError(t('channel.website.invalidUrl'));
        enqueueSnackbar(t('channel.website.invalidUrl'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
        return;
      }

      // Set default address if empty
      if (websiteAddress === '') {
        websiteAddress = 'https://app.aidasales.ir';
      }

      // Update the switch state to enabled and set the website address
      setFormData(prev => ({
        ...prev,
        website_chatbox_enabled: true,
        website_address: websiteAddress,
      }));

      const channelInfo: ChannelInfo_post = {
        telegram_enabled: formData.telegram_enabled,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: true,
        website_address: websiteAddress,
        bale_enabled: formData.bale_enabled,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: formData.instagram_enabled,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      enqueueSnackbar(t('channel.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      handleCloseModal('website');
    } catch (err: any) {
      console.error('Error saving Website settings:', err);
      enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      // Revert the switch state if save failed
      setFormData(prev => ({
        ...prev,
        website_chatbox_enabled: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  const saveBaleSettings = async () => {
    try {
      setLoading(true);
      
      // Update the switch state to enabled
      setFormData(prev => ({
        ...prev,
        bale_enabled: true,
      }));
      
      const channelInfo: ChannelInfo_post = {
        telegram_enabled: formData.telegram_enabled,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: formData.website_chatbox_enabled,
        website_address: formData.website_address.trim(),
        bale_enabled: true,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: formData.instagram_enabled,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      enqueueSnackbar(t('channel.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      handleCloseModal('bale');
    } catch (err: any) {
      console.error('Error saving Bale settings:', err);
      enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      // Revert the switch state if save failed
      setFormData(prev => ({
        ...prev,
        bale_enabled: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  // Handle modal save and close
  const handleModalSave = (modalType: keyof typeof modalOpen) => {
    switch (modalType) {
      case 'instagram':
        saveInstagramSettings();
        break;
      case 'telegram':
        saveTelegramSettings();
        break;
      case 'website':
        saveWebsiteSettings();
        break;
      case 'bale':
        saveBaleSettings();
        break;
      default:
        handleCloseModal(modalType);
    }
  };

  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  // Fetch subscription type
  useEffect(() => {
    const fetchSubscriptionType = async () => {
      try {
        const response = await getSubscriptionInfo();
        if (response.has_active_subscription && response.data) {
          setSubscriptionType(response.data.subscription_type);
        } else {
          setSubscriptionType(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription info:', error);
        setSubscriptionType(null);
      }
    };
    fetchSubscriptionType();
  }, []);

  useEffect(() => {
    const fetchChannelData = async () => {
      try {
        const [channelResponse, instagramResponse, personalInfoResponse , userResponse] = await Promise.all([
          onboarding.getChannelInfo(),
          onboarding.getInstagramStatus(),
          onboarding.getPersonalInfo(),
          auth.getCurrentUser(),
        ]);

        
        const data: ChannelInfo = channelResponse.data;
        const instagramData: InstagramStatus = instagramResponse.data;
        const personalData = personalInfoResponse.data;
        const userData = userResponse.data;

        setUserID(userData.user.id);
        setPhoneNumber(personalData.phone_number);
        setFormData({
          instagram_enabled: data.instagram_enabled,
          telegram_bot_token: data.telegram_bot_token || '',
          telegram_enabled: data.telegram_enabled,  
          website_chatbox_id: data.website_chatbox_id || '',
          website_chatbox_enabled: data.website_chatbox_enabled,
          website_address: data.website_address || '',
          bale_bot_token: data.bale_bot_token || '',
          bale_enabled: data.bale_enabled,
          kavenegar_api_key: data.kavenegar_api_key || '',
          kavenegar_enabled: data.kavenegar_enabled,
        });

        setInstagramStatus(instagramData);
      } catch (err: any) {
        console.error('Error fetching channel data:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
          enqueueSnackbar(t('channel.error.notFound'), { 
            variant: 'error',
            autoHideDuration: 4000,
            style: {
              padding: '10px',
              marginTop: '20px', // Add margin top
            },
          });
        } else if (err.response?.status === 400) {
          enqueueSnackbar(t('channel.error.incomplete'), { 
            variant: 'error',
            autoHideDuration: 4000,
            style: {
              padding: '10px',
              marginTop: '20px', // Add margin top
            },
          });
        } else {
          if (!flag) {
            enqueueSnackbar(t('channel.error.sessionExpired'), { 
              variant: 'error',
              autoHideDuration: 4000,
              style: {
                padding: '10px',
                marginTop: '20px', // Add margin top
              },
            });
            flag = true;
          }
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/auth/auth';
          }, 3000);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchChannelData();
  }, [t]);

  // Comprehensive URL validation function
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Allow empty URLs
    
    // Basic URL structure validation
    const urlPattern = /^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/;
    
    if (!urlPattern.test(url)) {
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      
      // Check protocol
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }
      
      // Check hostname (domain)
      const hostname = urlObj.hostname;
      
      // Hostname should not be empty
      if (!hostname || hostname.length === 0) {
        return false;
      }
      
      // Hostname should not start or end with dot
      if (hostname.startsWith('.') || hostname.endsWith('.')) {
        return false;
      }
      
      // Hostname should not have consecutive dots
      if (hostname.includes('..')) {
        return false;
      }
      
      // Check for valid TLD (Top Level Domain)
      const parts = hostname.split('.');
      if (parts.length < 2) {
        return false; // At least domain.tld
      }
      
      const tld = parts[parts.length - 1];
      // TLD should be at least 2 characters and contain only letters
      if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
        return false;
      }
      
      // Domain name should not be too short
      const domain = parts[parts.length - 2];
      if (domain.length < 2) {
        return false;
      }
      
      // Check for valid characters in domain parts
      const domainPartPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!domainPartPattern.test(parts[i])) {
          return false;
        }
      }
      
      return true;
    } catch {
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Validate website address
    if (name === 'website_address') {
      if (value.trim() && !validateUrl(value)) {
        setWebsiteAddressError(t('channel.website.invalidUrl'));
      } else {
        setWebsiteAddressError('');
      }
    }
  };

  const handleInstagramConnect = async () => {
    try {
      setInstagramLoading(true);
      const response = await onboarding.authorizeInstagram();
      const data = response.data;
      
      if (data.authorization_url) {
        // Open authorization URL in a new window
        const authWindow = window.open(data.authorization_url, '_blank', 'width=600,height=700');
        
        // Poll for window closure to check if authorization was successful
        const pollTimer = setInterval(async () => {
          if (authWindow?.closed) {
            clearInterval(pollTimer);
            // Refetch Instagram status to see if connection was successful
            try {
              const statusResponse = await onboarding.getInstagramStatus();
              setInstagramStatus(statusResponse.data);
              if (statusResponse.data.connected) {
                // Enable Instagram switch automatically after successful connection
                setFormData(prev => {
                  const updatedFormData = {
                    ...prev,
                    instagram_enabled: true,
                  };
                  
                  // Save the enabled state to backend
                  const channelInfo: ChannelInfo_post = {
                    telegram_enabled: updatedFormData.telegram_enabled,
                    telegram_bot_token: updatedFormData.telegram_bot_token.trim(),
                    website_chatbox_enabled: updatedFormData.website_chatbox_enabled,
                    website_address: updatedFormData.website_address.trim(),
                    bale_enabled: updatedFormData.bale_enabled,
                    bale_bot_token: updatedFormData.bale_bot_token.trim(),
                    kavenegar_enabled: updatedFormData.kavenegar_enabled,
                    kavenegar_api_key: updatedFormData.kavenegar_api_key.trim(),
                    instagram_enabled: true,
                    instagram_username: null,
                    instagram_password: null,
                  };
                  
                  // Save to backend asynchronously (don't await to avoid blocking)
                  onboarding.saveChannelInfo(channelInfo).catch((error) => {
                    console.error('Error saving Instagram enabled state:', error);
                  });
                  
                  return updatedFormData;
                });
                
                enqueueSnackbar(t('channel.instagram.connectSuccess'), { 
                  variant: 'success',
                  autoHideDuration: 4000,
                  style: {
                    padding: '10px',
                    marginTop: '20px',
                  },
                });
              }
            } catch (error) {
              console.error('Error checking Instagram status:', error);
            }
            setInstagramLoading(false);
          }
        }, 1000);
        
        // Cleanup timer after 5 minutes if window is still open
        setTimeout(() => {
          clearInterval(pollTimer);
          setInstagramLoading(false);
        }, 300000);
      }
    } catch (error: any) {
      console.error('Error connecting to Instagram:', error);
      enqueueSnackbar(t('channel.instagram.connectError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      setInstagramLoading(false);
    }
  };

  const handleInstagramDisconnect = async () => {
    try {
      setInstagramLoading(true);
      await onboarding.disconnectInstagram();
      
      // Update Instagram status
      const statusResponse = await onboarding.getInstagramStatus();
      setInstagramStatus(statusResponse.data);
      
      // Disable Instagram switch automatically after disconnect
      setFormData(prev => ({
        ...prev,
        instagram_enabled: false,
      }));
      
      // Save the disabled state to backend
      const channelInfo: ChannelInfo_post = {
        telegram_enabled: formData.telegram_enabled,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: formData.website_chatbox_enabled,
        website_address: formData.website_address.trim(),
        bale_enabled: formData.bale_enabled,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: false,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      
      enqueueSnackbar(t('channel.instagram.disconnectSuccess'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
    } catch (error: any) {
      console.error('Error disconnecting from Instagram:', error);
      enqueueSnackbar(t('channel.instagram.disconnectError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
    } finally {
      setInstagramLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Determine the website address to use
    let websiteAddress = formData.website_address.trim();
    
    // Validate website address before submission
    if (websiteAddress && !validateUrl(websiteAddress)) {
      setWebsiteAddressError(t('channel.website.invalidUrl'));
      enqueueSnackbar(t('channel.website.invalidUrl'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px', // Add margin top
        },
      });
      return;
    }

    // Set default address if empty and website chatbox is enabled
    if (formData.website_chatbox_enabled && websiteAddress === '') {
      websiteAddress = 'https://app.aidasales.ir';
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Update formData with the final website address
      setFormData(prev => ({
        ...prev,
        website_address: websiteAddress,
      }));
      
      const channelInfo: ChannelInfo_post = {
        telegram_enabled: formData.telegram_enabled,
        telegram_bot_token: formData.telegram_bot_token.trim(),
        website_chatbox_enabled: formData.website_chatbox_enabled,
        website_address: websiteAddress,
        bale_enabled: formData.bale_enabled,
        bale_bot_token: formData.bale_bot_token.trim(),
        kavenegar_enabled: formData.kavenegar_enabled,
        kavenegar_api_key: formData.kavenegar_api_key.trim(),
        instagram_enabled: formData.instagram_enabled,
        instagram_username: null,
        instagram_password: null,
      };
      
      await onboarding.saveChannelInfo(channelInfo);
      enqueueSnackbar(t('channel.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px', // Add margin top
        },
      });
    } catch (err: any) {
      if (err.response?.status === 400) {
        enqueueSnackbar(t('channel.error.incomplete'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px', // Add margin top
          },
        });
      } else if (err.response?.status === 500) {
        enqueueSnackbar(t('channel.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px', // Add margin top
          },
        });
      } else {
        enqueueSnackbar(err.response?.data?.detail?.[0]?.msg || t('channel.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px', // Add margin top
          },
        });
      }
      console.error('Error saving channel info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="warning">
            {error}
          </Alert>
        </Paper>
      </Box>
    );
  }

  const typographyResponsive = {
    '& .MuiTypography-h5': { fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } },
    '& .MuiTypography-body1': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
    '& .MuiTypography-body2': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } },
    '& .MuiTypography-caption': { fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' } },
    '& .MuiTypography-subtitle1': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' } },
    '& .MuiAlert-message': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
    '& .MuiDialogTitle-root': { fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } },
    '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } },
    '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
    '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
    '& .MuiChip-label': { fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } },
  };

  return (
    <Box sx={typographyResponsive}>
      <Box sx={{ p: 3, mb: 3 }}>
        <div className="first-step">
        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } }}>
          {t('channel.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }}>
          {t('channel.description')}
        </Typography>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}


        <Box sx={{
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 400px))', 
          gap: '40px', 
          justifyContent: 'center',
          alignItems: 'start',
          maxWidth: '1400px',
          margin: '0 auto',
          marginTop: '40px'
        }}>
          
          {/* Instagram Section */}
          {subscriptionType !== 'assistant' && (
          <Box sx={{border:'1px solid #E0E0E0' , padding:'20px' , borderRadius:'10px'  , boxShadow:'0 0 10px 0 rgba(0, 0, 0, 0.1)' , display:'flex', flexDirection:'column', gap:'15px'}} className="second-step">
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center' , flexWrap:'wrap'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Box sx={{width:'34px' , height:'34px' , padding:'5px' , borderRadius:'50%' , backgroundColor:'#F8BBD0', display:'flex', alignItems:'center', justifyContent:'center'}}><img src="./images/instagram-2016-logo-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px'}}/></Box>
                <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.instagram.title')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Chip 
                  label={formData.instagram_enabled ? t('channel.active') : t('channel.inactive')}
                  color={formData.instagram_enabled ? 'success' : 'error'}
                  variant="filled"
                  size="small"
                />
                <Switch 
                  checked={formData.instagram_enabled}
                  onChange={(e) => handleSwitchChange(e, 'instagram')}
                  name="instagram_enabled"
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            </Box>
            <Box>
              <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.instagram.description')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
              <Button 
                sx={{
                  flex: '5 1 0',
                  padding:'10px' , 
                  borderRadius:'8px', 
                  backgroundColor: formData.instagram_enabled ? '#C8E6C9' : '#E3F2FD',
                  color: formData.instagram_enabled ? '#2E7D32' : '#283593'
                }}
                onClick={() => handleOpenModal('instagram')}
              >
                {formData.instagram_enabled ? t('channel.instagram.settings') : t('channel.instagram.connect')}
              </Button>
              {!formData.instagram_enabled && (
                <Button 
                  onClick={() => window.open('https://app.aidasales.ir/pdfs/instagram.pdf', '_blank')}
                  sx={{
                    flex: '1 1 0',
                    padding:'10px' , 
                    borderRadius:'8px', 
                    minWidth: 'auto',
                    backgroundColor:'#E3F2FD',
                  }}
                >
                  <HelpIcon />
                </Button>
              )}
            </Box>
          </Box>
          )}

          {/* Telegram Section */}
          <Box sx={{border:'1px solid #E0E0E0' , padding:'20px' , borderRadius:'10px'  , boxShadow:'0 0 10px 0 rgba(0, 0, 0, 0.1)', display:'flex', flexDirection:'column', gap:'15px'}} className="third-step">
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap:'wrap'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Box sx={{width:'34px' , height:'34px' , padding:'5px' , borderRadius:'50%' , backgroundColor:'#BBDEFB', display:'flex', alignItems:'center', justifyContent:'center'}}><img src="./images/telegram-logo-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px'}}/></Box>
                <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.telegram.title')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Chip 
                  label={formData.telegram_enabled ? t('channel.active') : t('channel.inactive')}
                  color={formData.telegram_enabled ? 'success' : 'error'}
                  variant="filled"
                  size="small"
                />
                <Switch 
                  checked={formData.telegram_enabled}
                  onChange={(e) => handleSwitchChange(e, 'telegram')}
                  name="telegram_enabled"
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            </Box>
            <Box>
              <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.telegram.description')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
              <Button 
                sx={{
                  flex: '5 1 0',
                  padding:'10px' , 
                  borderRadius:'8px', 
                  backgroundColor: formData.telegram_enabled ? '#C8E6C9' : '#E3F2FD',
                  color: formData.telegram_enabled ? '#2E7D32' : '#283593'
                }}
                onClick={() => handleOpenModal('telegram')}
              >
                {formData.telegram_enabled ? t('channel.telegram.settings') : t('channel.telegram.connect')}
              </Button>
              {!formData.telegram_enabled && (
                <Button 
                  onClick={() => window.open('https://app.aidasales.ir/pdfs/telegram.pdf', '_blank')}
                  sx={{
                    flex: '1 1 0',
                    padding:'10px' , 
                    borderRadius:'8px', 
                    minWidth: 'auto',
                    backgroundColor:'#E3F2FD',
                  }}
                >
                  <HelpIcon />
                </Button>
              )}
            </Box>
          </Box>

          {/* Website Section */}
          <Box sx={{border:'1px solid #E0E0E0' , padding:'20px' , borderRadius:'10px'  , boxShadow:'0 0 10px 0 rgba(0, 0, 0, 0.1)', display:'flex', flexDirection:'column', gap:'15px'}} className="fourth-step">
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap:'wrap'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Box sx={{width:'34px' , height:'34px' , padding:'5px' , borderRadius:'50%' , backgroundColor:'#FFCCBC', display:'flex', alignItems:'center', justifyContent:'center'}}><img src="./images/website-click-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px'}}/></Box>
                <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.website.title')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Chip 
                  label={formData.website_chatbox_enabled ? t('channel.active') : t('channel.inactive')}
                  color={formData.website_chatbox_enabled ? 'success' : 'error'}
                  variant="filled"
                  size="small"
                />
                <Switch 
                  checked={formData.website_chatbox_enabled}
                  onChange={(e) => handleSwitchChange(e, 'website')}
                  name="website_chatbox_enabled"
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            </Box>
            <Box>
              <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.website.description')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
              <Button 
                sx={{
                  flex: '5 1 0',
                  padding:'10px' , 
                  borderRadius:'8px', 
                  backgroundColor: formData.website_chatbox_enabled ? '#C8E6C9' : '#E3F2FD',
                  color: formData.website_chatbox_enabled ? '#2E7D32' : '#283593'
                }}
                onClick={() => handleOpenModal('website')}
              >
                {formData.website_chatbox_enabled ? t('channel.website.settings') : t('channel.website.connect')}
              </Button>
              {!formData.website_chatbox_enabled && (
                <Button 
                  onClick={() => window.open('https://app.aidasales.ir/pdfs/website.pdf', '_blank')}
                  sx={{
                    flex: '1 1 0',
                    padding:'10px' , 
                    borderRadius:'8px', 
                    minWidth: 'auto',
                    backgroundColor:'#E3F2FD',
                  }}
                >
                  <HelpIcon />
                </Button>
              )}
            </Box>
          </Box>

          {/* Bale Section */}
          <Box sx={{border:'1px solid #E0E0E0' , padding:'20px' , borderRadius:'10px'  , boxShadow:'0 0 10px 0 rgba(0, 0, 0, 0.1)', display:'flex', flexDirection:'column', gap:'15px'}} className="fifth-step">
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap:'wrap'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Box sx={{width:'34px' , height:'34px' , padding:'5px' , borderRadius:'50%' , backgroundColor:'#DCEDC8', display:'flex', alignItems:'center', justifyContent:'center'}}><img src="./images/bale-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px'}}/></Box>
                <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.bale.title')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Chip 
                  label={formData.bale_enabled ? t('channel.active') : t('channel.inactive')}
                  color={formData.bale_enabled ? 'success' : 'error'}
                  variant="filled"
                  size="small"
                />
                <Switch 
                  checked={formData.bale_enabled}
                  onChange={(e) => handleSwitchChange(e, 'bale')}
                  name="bale_enabled"
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            </Box>
            <Box>
              <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.bale.description')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
              <Button 
                sx={{
                  flex: '5 1 0',
                  padding:'10px' , 
                  borderRadius:'8px', 
                  backgroundColor: formData.bale_enabled ? '#C8E6C9' : '#E3F2FD',
                  color: formData.bale_enabled ? '#2E7D32' : '#283593'
                }}
                onClick={() => handleOpenModal('bale')}
              >
                {formData.bale_enabled ? t('channel.bale.settings') : t('channel.bale.connect')}
              </Button>
              {!formData.bale_enabled && (
                <Button 
                  onClick={() => window.open('https://app.aidasales.ir/pdfs/bale.pdf', '_blank')}
                  sx={{
                    flex: '1 1 0',
                    padding:'10px' , 
                    borderRadius:'8px', 
                    minWidth: 'auto',
                    backgroundColor:'#E3F2FD',
                  }}
                >
                  <HelpIcon />
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        </form>
      </Box>

      {/* Instagram Modal */}
      {subscriptionType !== 'assistant' && (
      <Dialog 
        open={modalOpen.instagram} 
        onClose={() => handleCloseModal('instagram')}
        sx={{
          maxWidth:'560px',
          margin:'0 auto',
        }}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: '34px', height: '34px', padding: '5px', borderRadius: '50%', backgroundColor: '#F8BBD0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="./images/instagram-2016-logo-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px' }} /></Box>
            <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.instagram.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }, '& .MuiAlert-message': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } } }}>
          <Box>
            <Box sx={{ mb: 2 }}>
              {instagramStatus.connected ? (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('channel.instagram.connected')} 
                    {instagramStatus.username && ` (@${instagramStatus.username})`}
                    {instagramStatus.token_expires_at && (
                      <Typography variant="caption" display="block" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' } }}>
                        {t('channel.instagram.tokenExpires')}: {formatDate(instagramStatus.token_expires_at)}
                      </Typography>
                    )}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleInstagramDisconnect}
                    disabled={instagramLoading}
                    sx={{ mr: 2 }}
                  >
                    {instagramLoading ? t('channel.instagram.disconnecting') : t('channel.instagram.disconnect')}
                  </Button>
                  {!instagramStatus.token_valid && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t('channel.instagram.tokenInvalid')}
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {/* {t('channel.instagram.notConnected')} */}
                    {t('channel.instagram.Connent_help')}
                  </Alert>
                  <Box sx={{
                    display: 'flex', 
                    justifyContent: 'space-around', 
                    alignItems: 'center', 
                    gap: '10px',
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& .MuiButton-root': {
                      minWidth: { xs: '120px', sm: 'auto' },
                      fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                      padding: { xs: '6px 12px', sm: '8px 16px' }
                    }
                  }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                          const startParam = `${phoneNumber}_${userID}`;
                          const encodedStart = encodeURIComponent(startParam);
                          window.open(`https://t.me/TheAida_Bot?start=${encodedStart}`, '_blank');
                        }}
                        disabled={instagramLoading}
                        size="small"
                        sx={{ 
                          display: 'none',
                          minWidth: { xs: '120px', sm: 'auto' },
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                          padding: { xs: '6px 12px', sm: '8px 16px' }
                        }}
                      >
                        {t('channel.instagram.register_Bot')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleInstagramConnect}
                        disabled={instagramLoading}
                        size="small"
                        sx={{ 
                          minWidth: { xs: '120px', sm: 'auto' },
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                          padding: { xs: '6px 12px', sm: '8px 16px' }
                        }}
                      >
                        {instagramLoading ? t('channel.instagram.connecting') : t('channel.instagram.connect')}
                      </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } } }}>
          <Button 
            onClick={() => handleCloseModal('instagram')}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => handleModalSave('instagram')} 
            disabled={loading || !instagramStatus.connected}
          >
            {loading ? t('channel.saving') : t('channel.save')}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Telegram Modal */}
      <Dialog 
        open={modalOpen.telegram} 
        onClose={() => handleCloseModal('telegram')}
        sx={{
          maxWidth: '560px',
          margin: '0 auto',
        }}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: '34px', height: '34px', padding: '5px', borderRadius: '50%', backgroundColor: '#BBDEFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="./images/telegram-logo-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px' }} /></Box>
            <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.telegram.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }, '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.telegram.descriptionHelper')}</Typography>
            <TextField
              fullWidth
              label={t('channel.telegram.bot_token')}
              name="telegram_bot_token"
              value={formData.telegram_bot_token}
              onChange={handleChange}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } } }}>
          <Button 
            onClick={() => handleCloseModal('telegram')}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => handleModalSave('telegram')} 
            disabled={loading || !formData.telegram_bot_token.trim()}
          >
            {loading ? t('channel.saving') : t('channel.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Website Modal */}
      <Dialog 
        open={modalOpen.website} 
        onClose={() => handleCloseModal('website')}
        sx={{
          maxWidth: '560px',
          margin: '0 auto',
        }}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: '34px', height: '34px', padding: '5px', borderRadius: '50%', backgroundColor: '#FFCCBC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="./images/website-click-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px' }} /></Box>
            <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.website.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }, '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } } }}>
          <Box>
            <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.website.descriptionHelper')}</Typography>
            <TextField
              fullWidth
              label={t('channel.website.chatbox_id')}
              name="website_chatbox_id"
              value={formData.website_chatbox_id}
              onChange={handleChange}
              margin="normal"
              required
              disabled
            />
            <TextField
              fullWidth
              label={t('channel.website.website_address')}
              name="website_address"
              value={formData.website_address}
              onChange={handleChange}
              margin="normal"
              error={!!websiteAddressError}
              helperText={websiteAddressError}
              placeholder="https://example.com"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } } }}>
          <Button 
            onClick={() => handleCloseModal('website')}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={() => handleModalSave('website')} disabled={loading}>
            {loading ? t('channel.saving') : t('channel.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bale Modal */}
      <Dialog 
        open={modalOpen.bale} 
        onClose={() => handleCloseModal('bale')}
        sx={{
          maxWidth: '560px',
          margin: '0 auto',
        }}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: '34px', height: '34px', padding: '5px', borderRadius: '50%', backgroundColor: '#DCEDC8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="./images/bale-svgrepo-com.svg" alt="" style={{ width: '20px', height: '20px' }} /></Box>
            <Typography component="p" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' }, fontWeight: 'bold' }}>{t('channel.bale.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }, '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Typography component="p" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, color: '#666' }}>{t('channel.bale.descriptionHelper')}</Typography>
              <TextField
                fullWidth
                label={t('channel.bale.bot_token')}
                name="bale_bot_token"
                value={formData.bale_bot_token}
                onChange={handleChange}
                margin="normal"
                required
              />
            </Box>
        </DialogContent>
        <DialogActions sx={{ '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } } }}>
          <Button 
            onClick={() => handleCloseModal('bale')}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => handleModalSave('bale')} 
            disabled={loading || !formData.bale_bot_token.trim()}
          >
            {loading ? t('channel.saving') : t('channel.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Channel: FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_channel}>
      <VideoProvider videoUrl={videoUrls.channel} videoTitle={videoTitles.channel}>
        <ChannelContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default Channel;